/**
 * Who does what — v19 D2. Route `team/throughput`; data from the bare `GET /throughput`
 * (scope = the TOKEN's `reports.view` countries, echoed; see `team-api.ts`).
 *
 * ===========================================================================
 * A MANAGEMENT VIEW, NOT A SCOREBOARD
 * ===========================================================================
 * The brief: "report what happened, do not rank people". So: people are listed in the
 * order the server sends them (NAME), there is no rank column, no medal, no top-N, no
 * sort-by-count control and no percentage-of-team figure. Four facts per person — CRFs
 * delivered, test rounds recorded, deployments, sign-offs (developer and project as two
 * columns, because they are two different signatures) — plus the (country × role) team
 * roll-up, which is the only organisational axis the data has (proposal §8 #8). A note
 * under the table SAYS it is not a ranking, so nobody reads the first row as the best.
 *
 * ===========================================================================
 * THE PERIOD IS THE COUNTRY'S, AND THE SCREEN SAYS WHICH
 * ===========================================================================
 * `?period=` (+ `from`/`to` for `custom`) rides the URL. The server resolves the key PER
 * COUNTRY in that country's calendar and returns one `windows[]` row per admitted country;
 * the line under the chips prints every one ("IN 01 Aug – 31 Aug 2026 · AE …"), because for
 * a six-country seat "this month" is six windows that do not share an instant. The chips
 * say what was ASKED; that line says what was USED.
 *
 * NO MONEY ANYWHERE on this screen (proposal §8 #26) — `team-throughput.test.tsx` asserts
 * the DOM carries no currency and no masked marker.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Card, CountryBadge, EmptyState, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import { DEFAULT_DASHBOARD_PERIOD, formatDateOnlyRange, readPeriodQuery } from './periods';
import type { DashboardPeriod } from './periods';
import { fetchThroughput, teamErrorText, teamKeys } from './team-api';
import type { CountryWindowDto, PersonThroughputDto, TeamThroughputDto, ThroughputDto, ThroughputFilters } from './team-api';
import { TeamPeriodControls } from './TeamPeriodControls';

export function TeamThroughputPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  // The ROUTE's key — `@RequirePerm('reports.view')` on both throughput routes.
  const canView = can(countryCode, 'reports.view');

  const [params, setParams] = useSearchParams();
  const asked = readPeriodQuery(params);
  const period = asked.period ?? DEFAULT_DASHBOARD_PERIOD;
  const from = asked.from ?? '';
  const to = asked.to ?? '';

  const filters = useMemo<ThroughputFilters>(
    () => ({ period, from: from === '' ? undefined : from, to: to === '' ? undefined : to }),
    [period, from, to],
  );

  const query = useQuery({
    queryKey: teamKeys.throughput(filters),
    queryFn: ({ signal }) => fetchThroughput(filters, signal),
    enabled: canView,
  });

  /** Period changes REPLACE history — flipping chips is not a trail worth walking back. */
  const patch = (apply: (next: URLSearchParams) => void): void => {
    const next = new URLSearchParams(params);
    apply(next);
    setParams(next, { replace: true });
  };
  const setPeriod = (value: DashboardPeriod): void => {
    patch((next) => {
      next.set('period', value);
      // A quick period has no custom bounds; leaving them would make the URL describe a
      // window the server did not apply.
      next.delete('from');
      next.delete('to');
    });
  };
  const setCustom = (which: 'from' | 'to', value: string): void => {
    patch((next) => {
      next.set('period', 'custom');
      if (value === '') next.delete(which);
      else next.set(which, value);
    });
  };

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Who does what is not available to this account">
          Seeing the team's throughput needs the reports permission. Ask an administrator to
          widen your access.
        </Alert>
      </div>
    );
  }

  const data = query.data;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Who does what"
        subtitle={
          data === undefined
            ? undefined
            : `${String(data.people.length)} ${data.people.length === 1 ? 'person' : 'people'} with activity · ${data.countries.join(', ')}`
        }
      />

      <TeamPeriodControls period={period} from={from} to={to} onPeriod={setPeriod} onCustom={setCustom} />

      {data !== undefined ? <ResolvedWindows windows={data.windows} /> : null}

      {query.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading who does what" />
        </div>
      ) : query.isError ? (
        <Alert tone="danger" title="Who does what could not be loaded">
          {/* The server's own sentence — on a 400 it names the period vocabulary. */}
          <p>{teamErrorText(query.error)}</p>
          <button
            type="button"
            onClick={() => {
              void query.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : (
        <ThroughputBody data={query.data} />
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────── resolved windows ── */

/**
 * THE WINDOWS THE SERVER APPLIED, IN WORDS, ONE PER COUNTRY. Not decoration: the chips say
 * what was ASKED for and this says what was USED, each in its country's calendar.
 */
function ResolvedWindows({ windows }: { windows: readonly CountryWindowDto[] }): React.JSX.Element {
  if (windows.length === 0) return <p className="text-body13 text-fg" data-testid="resolved-window">No country in scope.</p>;
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="resolved-window" aria-label="Window applied per country">
      {windows.map((window) => (
        <li key={window.country_code} className="inline-flex items-center gap-1.5 text-body13 text-fg">
          <CountryBadge code={window.country_code} />
          <span className="tnum">{formatDateOnlyRange(window.from, window.to)}</span>
        </li>
      ))}
    </ul>
  );
}

/* ──────────────────────────────────────────────────────────────────── body ── */

function ThroughputBody({ data }: { data: ThroughputDto }): React.JSX.Element {
  return (
    <>
      {data.unattributed.crfs_delivered > 0 ? (
        <Alert tone="info" title={`${String(data.unattributed.crfs_delivered)} ${data.unattributed.crfs_delivered === 1 ? 'CRF was' : 'CRFs were'} delivered through a work order with no assignee`}>
          Counted in the totals, attributed to nobody — the columns reconcile, the person is
          unknown.
        </Alert>
      ) : null}

      {data.people.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="Nothing happened in this window"
            description="No CRF was delivered, no test round recorded, no deployment or sign-off logged by anyone in scope."
          />
        </div>
      ) : (
        <>
          <Card title="People" className="gap-4">
            <PeopleTable people={data.people} />
            <p className="text-label text-fg-secondary">
              Listed by name. This is a record of what happened in the window, not a ranking —
              a person with no activity has no row.
            </p>
          </Card>
          <Card
            title={
              <h2 className="flex items-center gap-2 text-h3 text-fg">
                Teams
                <span className="text-body13 font-normal text-fg-secondary">country × role</span>
              </h2>
            }
            className="gap-4"
          >
            <TeamsTable teams={data.teams} />
          </Card>
        </>
      )}
    </>
  );
}

/* ───────────────────────────────────────────────────────────── people table ── */

const PEOPLE_HEADERS = [
  { label: 'NAME', numeric: false },
  { label: 'COUNTRY', numeric: false },
  { label: 'ROLES', numeric: false },
  { label: 'CRFS DELIVERED', numeric: true },
  { label: 'TEST ROUNDS', numeric: true },
  { label: 'DEPLOYMENTS', numeric: true },
  { label: 'DEV SIGN-OFFS', numeric: true },
  { label: 'PROJECT SIGN-OFFS', numeric: true },
] as const;

function PeopleTable({ people }: { people: readonly PersonThroughputDto[] }): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1040px] border-collapse text-left">
        <caption className="sr-only">What each person recorded in the window, listed by name</caption>
        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {PEOPLE_HEADERS.map((header) => (
              <th
                key={header.label}
                scope="col"
                className={cn('px-3 text-th uppercase text-fg', header.numeric && 'text-right')}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map((person) => (
            <tr
              key={`${person.user_id}-${person.country_code}`}
              className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle"
            >
              <td className="px-3 align-middle text-body font-medium text-fg">{person.name}</td>
              <td className="px-3 align-middle">
                <CountryBadge code={person.country_code} />
              </td>
              <td className="px-3 align-middle text-body13 text-fg">
                {person.roles.length === 0 ? <span className="text-fg-secondary">—</span> : person.roles.join(', ')}
              </td>
              <Count value={person.crfs_delivered} />
              <Count value={person.test_rounds} />
              <Count value={person.deployments} />
              <Count value={person.signoffs_developer} />
              <Count value={person.signoffs_project} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A count cell. Zero is quiet (`text-fg-secondary` — 4.64/5.85 on the surface), anything else reads. */
function Count({ value }: { value: number }): React.JSX.Element {
  return (
    <td className={cn('tnum px-3 text-right align-middle text-body', value === 0 ? 'text-fg-secondary' : 'text-fg')}>
      {value}
    </td>
  );
}

/* ─────────────────────────────────────────────────────────────── teams table ── */

const TEAM_HEADERS = [
  { label: 'COUNTRY', numeric: false },
  { label: 'ROLE', numeric: false },
  { label: 'PEOPLE', numeric: true },
  { label: 'CRFS DELIVERED', numeric: true },
  { label: 'TEST ROUNDS', numeric: true },
  { label: 'DEPLOYMENTS', numeric: true },
  { label: 'SIGN-OFFS', numeric: true },
] as const;

function TeamsTable({ teams }: { teams: readonly TeamThroughputDto[] }): React.JSX.Element {
  if (teams.length === 0) {
    return <EmptyState title="No team rows" description="Nobody with a role row recorded anything in this window." className="py-6" />;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[880px] border-collapse text-left">
        <caption className="sr-only">Activity rolled up by country and role</caption>
        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {TEAM_HEADERS.map((header) => (
              <th
                key={header.label}
                scope="col"
                className={cn('px-3 text-th uppercase text-fg', header.numeric && 'text-right')}
              >
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {teams.map((team) => (
            <tr
              key={`${team.country_code}-${String(team.role_name)}`}
              className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle"
            >
              <td className="px-3 align-middle">
                <CountryBadge code={team.country_code} />
              </td>
              <td className="px-3 align-middle text-body text-fg">
                {team.role_name ?? <span className="text-fg-secondary">no role row</span>}
              </td>
              <Count value={team.people} />
              <Count value={team.crfs_delivered} />
              <Count value={team.test_rounds} />
              <Count value={team.deployments} />
              <Count value={team.signoffs} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
